// src/components/WelcomeConfigModal.js

import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './WelcomeConfigModal.css';

const WelcomeConfigModal = ({ isOpen, onClose }) => {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checklist, setChecklist] = useState({
        cuotaMensual: false,
        corteDia: false,
        sancionesInicioDia: false,
    });

    useEffect(() => {
        if (!isOpen || !userData?.fraccionamientoId) return;

        const fetchConfig = async () => {
            try {
                setLoading(true);
                const fraccRef = doc(db, 'fraccionamientos', userData.fraccionamientoId);
                const snap = await getDoc(fraccRef);
                const data = snap.exists() ? snap.data() : {};
                const cfg = data.configuracionFinanciera || {};

                setConfig(cfg);

                // Validar campos requeridos
                const cuotaOk = cfg.montoMensual != null && Number(cfg.montoMensual) > 0;
                const corteOk = cfg.corteDia != null && cfg.corteDia !== '';
                const sancionOk = cfg.sancionesInicioDia != null && cfg.sancionesInicioDia !== '';

                setChecklist({
                    cuotaMensual: cuotaOk,
                    corteDia: corteOk,
                    sancionesInicioDia: sancionOk,
                });
            } catch (error) {
                console.error('WelcomeConfigModal: error al cargar config', error);
            } finally {
                setLoading(false);
            }
        };

        fetchConfig();
    }, [isOpen, userData]);

    const allComplete = checklist.cuotaMensual && checklist.corteDia && checklist.sancionesInicioDia;

    const handleGoToConfig = () => {
        onClose();
        navigate('/configuraciones/financiera');
    };

    const handleContinue = () => {
        if (allComplete) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="welcome-config-modal-overlay">
            <div className="welcome-config-modal-content">
                <div className="welcome-config-modal-header">
                    <h2>🏠 Bienvenido al Sistema de Gestión Financiera Domus Habitat</h2>
                </div>

                <div className="welcome-config-modal-body">
                    {loading ? (
                        <p className="loading-text">Verificando configuración...</p>
                    ) : (
                        <>
                            <p className="intro-text">
                                Para comenzar a utilizar el sistema de cargos mensuales automáticos, 
                                es necesario completar la configuración financiera básica.
                            </p>

                            <div className="checklist-container">
                                <h3>Configuración requerida:</h3>
                                <div className="checklist-items">
                                    <div className={`checklist-item ${checklist.cuotaMensual ? 'complete' : 'incomplete'}`}>
                                        <span className="checklist-icon">
                                            {checklist.cuotaMensual ? '✓' : '○'}
                                        </span>
                                        <div className="checklist-text">
                                            <strong>Cuota mensual</strong>
                                            <small>Define el monto de la cuota mensual ordinaria</small>
                                        </div>
                                    </div>

                                    <div className={`checklist-item ${checklist.corteDia ? 'complete' : 'incomplete'}`}>
                                        <span className="checklist-icon">
                                            {checklist.corteDia ? '✓' : '○'}
                                        </span>
                                        <div className="checklist-text">
                                            <strong>Día de corte</strong>
                                            <small>Establece el día del mes para cerrar el ciclo de facturación</small>
                                        </div>
                                    </div>

                                    <div className={`checklist-item ${checklist.sancionesInicioDia ? 'complete' : 'incomplete'}`}>
                                        <span className="checklist-icon">
                                            {checklist.sancionesInicioDia ? '✓' : '○'}
                                        </span>
                                        <div className="checklist-text">
                                            <strong>Día de inicio de sanciones</strong>
                                            <small>Define cuándo empiezan a aplicarse multas e intereses</small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {!allComplete && (
                                <div className="warning-box">
                                    <p>
                                        ⚠️ <strong>La configuración está incompleta.</strong> 
                                        Por favor completa los campos requeridos antes de usar el sistema.
                                    </p>
                                </div>
                            )}

                            {allComplete && (
                                <div className="success-box">
                                    <p>
                                        ✓ <strong>¡Configuración completa!</strong> 
                                        Ya puedes empezar a usar todas las funciones del sistema.
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="welcome-config-modal-footer">
                    {!allComplete && (
                        <button 
                            className="btn-go-config" 
                            onClick={handleGoToConfig}
                            disabled={loading}
                        >
                            Ir a Configuración Financiera
                        </button>
                    )}
                    {allComplete && (
                        <button 
                            className="btn-continue" 
                            onClick={handleContinue}
                        >
                            Continuar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WelcomeConfigModal;
